import React, { forwardRef, KeyboardEventHandler, useCallback, useImperativeHandle, useMemo, useRef, useId } from "react";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import dayjs from "dayjs";
import BasicDatePicker from "../basic-date";
import clsx from "clsx";

type BasicTextProps = {
    label?: string;
    value: string;
    onChange?: (value: string) => void;
    type?: React.HTMLInputTypeAttribute;
    placeholder?: string;
    center?: boolean;
    disabled?: boolean;
    required?: boolean;
    onEnter?: () => void;
    className?: string;
    suggestions?: string[];
};

export type BasicTextHandle = {
    focus: () => void;
};

const BasicText = forwardRef<BasicTextHandle, BasicTextProps>((props, ref) => {
    const listID = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const [showPassword, setShowPassword] = React.useState(false);

    const focus = useCallback(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    useImperativeHandle(ref, () => ({
        focus,
    }));

    const type = useMemo(() => {
        if (props.type !== "password") return props.type || "text";
        return showPassword ? "text" : "password";
    }, [props.type, showPassword]);

    const handleKeyDown: KeyboardEventHandler = (event) => {
        if (event.key !== "Enter") return;
        props.onEnter?.();
    };

    const onChange = useCallback(
        (value: string) => {
            props.onChange?.(value);
        },
        [props.onChange]
    );

    dayjs.locale("en-gb");

    const className = useMemo(() => {
        const center = props.center ? "text-center" : "";
        const disabled = props.disabled ? "opacity-70 cursor-not-allowed" : "";
        return `h-10 w-full shadow-md rounded-md max-h-20 px-4 outline-none bg-white ${center} ${disabled}`;
    }, [props.center, props.disabled]);

    if (props.type === "date") {
        return (
            <BasicDatePicker
                ref={ref}
                label={props.label}
                value={props.value}
                onChange={props.onChange}
                placeholder={props.placeholder}
                disabled={props.disabled}
                required={props.required}
                className={props.className}
            />
        );
    }

    return (
        <div className={`flex flex-col relative ${props.className}`}>
            {props.label != undefined && (
                <label className="">
                    {props.label}
                    {props.required && <span className="text-dark-red"> *</span>}
                </label>
            )}
            <div className="relative">
                {props.type === "password" && (
                    <div
                        className="absolute bottom-0 right-4 flex items-center opacity-80 cursor-pointer"
                        onClick={() => setShowPassword(!showPassword)}
                    >
                        {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </div>
                )}
                <input
                    ref={inputRef}
                    className={className}
                    value={props.value}
                    onChange={(e) => onChange(e.target.value)}
                    type={type}
                    placeholder={props.placeholder}
                    disabled={props.disabled}
                    onKeyDown={handleKeyDown}
                    list={props.suggestions ? listID : undefined}
                />
                {props.suggestions && props.suggestions.length > 0 && (
                    <datalist id={listID}>
                        {props.suggestions.map((suggestion, index) => (
                            <option value={suggestion} key={index} />
                        ))}
                    </datalist>
                )}
            </div>
        </div>
    );
});

BasicText.displayName = "BasicText";

export default BasicText;

type BasicNumberProps = {
    label?: string;
    onChange?: (value: number) => void;
    type: "number";
    placeholder?: string;
    value: number;
    min: number;
    max: number;
    disabled?: boolean;
    align?: "left" | "center" | "right";
};

export const BasicNumber: React.FC<BasicNumberProps> = (props) => {
    const { align = "center" } = props;

    const handleChange = (val: number) => {
        if (props.onChange) {
            props.onChange(val);
        }
    };

    const handleOnInput = (e: any) => {
        if (e.target.value > props.max) {
            e.target.value = props.max;
            handleChange(props.max);
        }
        if (e.target.value < props.min) {
            e.target.value = props.min;
            handleChange(props.min);
        }
    };

    const className = useMemo(
        () =>
            clsx("h-10 w-full shadow-md rounded-md max-h-20 px-4 outline-none bg-white", {
                "text-center": align === "center",
                "text-right": align === "right",
                "text-left": align === "left",
                "opacity-50": props.disabled,
            }),
        [align, props.disabled]
    );

    return (
        <div className="flex flex-col relative">
            {props.label != undefined && <label className="mb-1">{props.label}</label>}
            <input
                min={props.min}
                max={props.max}
                onBlur={handleOnInput}
                className={className}
                value={props.value}
                onChange={(e) => handleChange(Number(e.target.value))}
                type="number"
                placeholder={props.placeholder}
                disabled={props.disabled}
            />
        </div>
    );
};

type BasicTextAreaProps = {
    label?: string;
    value: string;
    onChange?: (value: string) => void;
    disabled?: boolean;
    required?: boolean;
    placeholder?: string;
};

export const BasicTextArea: React.FC<BasicTextAreaProps> = (props) => {
    const className = useMemo(() => {
        const disabled = props.disabled ? "opacity-70 cursor-not-allowed" : "";
        return `w-full shadow-md rounded-md px-4 outline-none py-1 focus:outline focus:outline-blue ${disabled}`;
    }, [props.disabled]);

    return (
        <div className="flex flex-col relative">
            {props.label != undefined && (
                <label className="mb-1">
                    {props.label}
                    {props.required && <span className="text-dark-red"> *</span>}
                </label>
            )}
            <textarea
                className={className}
                value={props.value}
                onChange={(e) => props.onChange && props.onChange(e.target.value)}
                placeholder={props.placeholder}
                disabled={props.disabled}
                rows={6}
            />
        </div>
    );
};
